import axios from 'axios';
import axiosRetry from 'axios-retry';
import authConfig from '../constants/authConfig';
import * as constants from '../constants/constants';
import * as storageHelper from '../functions/storageHelper';
import { getOS, phoneDetect } from '../functions/utils';
import webIMConfig from '../libs/webIMConfig';
import logService from '../services/logService';
import Urls from './urls';

const configApp = require('../../app');

const isNative = !configApp || !configApp.isWebApp;

const PURCHASE_APIS = [Urls.UPGRADE_PURCHASE, Urls.POST_PURCHASE_CONSUMABLE_PRODUCT];

export default class RestService {
    authConfig = { ...authConfig };

    // ** For Refreshing Token
    isAlreadyFetchingAccessToken = false;

    // ** For Refreshing Token
    subscribers = [];

    constructor(authOverrideConfig) {
        this.authConfig = { ...this.authConfig, ...authOverrideConfig };
        this.isStorageCachingSupported = storageHelper.storageCachingSupported();

        // handle retry
        const retryConfig = {
            noOfRetry: 2,
            retryDelay: 1500, // Change from 3000 to 1500
            retryRanges: [
                [100, 199],
                [429, 429],
                [501, 599],
                [408, 408],
            ], // add 408 Request timeout to retryRanges
            chatRetryStatusCodeRanges: [
                [500, 502],
                [401, 401],
            ],
        };
        axiosRetry(axios, {
            retries: retryConfig.noOfRetry,
            retryDelay: retryCount => {
                return retryConfig.retryDelay;
            },
            retryCondition: error => {
                console.log('axiosRetry', error);
                // we do not retry purchase api
                if (PURCHASE_APIS.includes(error.config.url)) {
                    return false;
                }
                let retryRange = retryConfig.retryRanges;
                if (error && error.response && error.response.config && error.response.config.baseURL === Urls.BASE_URL) {
                    retryRange = retryConfig.chatRetryStatusCodeRanges;
                }
                if (error.response && error.response.status) {
                    return this.isInRetryRange(error.response.status, retryRange);
                }
                return false;
            },
        });

        // ** Request Interceptor
        axios.interceptors.request.use(
            config => {
                // ** Set timeout
                if (!config.timeout) {
                    config.timeout = 30000;
                }

                // ** Set custom headers
                if (config.baseURL === Urls.BASE_RHPURL || config.baseURL === Urls.AUT_RHPURL) {
                    config.headers.AppOrigin = this.deviceType();
                    config.headers.DeviceOS = this.osType();
                    if (webIMConfig.versions) {
                        config.headers.Version = webIMConfig.versions;
                    }
                }
                // ** Set Authorization header
                if (!config.headers.isAnonymous) {
                    // ** Get token from localStorage
                    const accessToken = this.getToken(config);

                    // ** If token is present add it to request's Authorization Header
                    if (accessToken) {
                        // ** eslint-disable-next-line no-param-reassign
                        config.headers.Authorization = `${this.authConfig.tokenType} ${accessToken}`;
                    }
                    if (!config.headers.Authorization) throw axios.Cancel('Token null')
                }

                // ** Remove custom headers used for authentication checking
                if (config.headers.hasOwnProperty('isAnonymousAccessToken')) {
                    delete config.headers.isAnonymousAccessToken;
                }
                if (config.headers.hasOwnProperty('isAnonymous')) {
                    delete config.headers.isAnonymous;
                }
                return config;
            },
            error => Promise.reject(error)
        );

        // ** Add request/response interceptor
        axios.interceptors.response.use(
            response => {
                // return response;
                return this.createResponse((response.config && response.config.baseURL) || undefined, response);
            },
            error => {
                // ** const { config, response: { status } } = error
                const { config, response } = error;
                const originalRequest = config;
                // ** if (status === 401) {
                const newRes = this.createResponse((config && config.baseURL) || undefined, response);
                if (newRes && newRes.status === 401 && config.baseURL === Urls.BASE_RHPURL) {
                    if (!this.isAlreadyFetchingAccessToken) {
                        this.isAlreadyFetchingAccessToken = true;
                        // originalBaseUrl, originalUrl, originalUrlResponse, originalRequestToken
                        this.refreshToken(config.baseURL, config.url, newRes.data, config.headers.Authorization).then(r => {
                            // console.log('refreshToken', r)
                            this.isAlreadyFetchingAccessToken = false;

                            // ** Update accessToken in localStorage
                            if (r && r.data && r.data.AccessToken) {
                                this.setToken(r.data.AccessToken);
                                this.onAccessTokenFetched(r.data.AccessToken);
                            }
                        });
                    }
                    const retryOriginalRequest = new Promise(resolve => {
                        this.addSubscriber(accessToken => {
                            // ** Make sure to assign accessToken according to your response.
                            // ** Check: https://pixinvent.ticksy.com/ticket/2413870
                            // ** Change Authorization header
                            originalRequest.headers.Authorization = `${this.authConfig.tokenType} ${accessToken}`;
                            resolve(axios(originalRequest));
                        });
                    });
                    return retryOriginalRequest;
                }

                if (config?.baseURL === Urls.BASE_URL) {
                    return Promise.resolve({
                        data: {
                            success: newRes?.success || false,
                            statusCode: newRes?.statusCode,
                            message: newRes?.message || newRes?.data?.message || newRes?.data?.Message || '',
                            data: newRes?.data,
                            status: newRes?.status || 500,
                        },
                        config,
                    });
                }
                return Promise.resolve({
                    success: newRes?.success || false,
                    statusCode: newRes?.statusCode,
                    message: newRes?.message || newRes?.data?.message || newRes?.data?.Message || '',
                    data: newRes?.data,
                    status: newRes?.status || 500,
                });
            }
        );
    }

    onAccessTokenFetched(accessToken) {
        this.subscribers = this.subscribers.filter(callback => callback(accessToken));
    }

    addSubscriber(callback) {
        this.subscribers.push(callback);
    }

    getToken(axiosConfig) {
        let token = null;
        if (axiosConfig.baseURL === Urls.BASE_RHPURL || axiosConfig.baseURL === Urls.NEW_BASE_RHPURL || axiosConfig.baseURL === Urls.AUT_RHPURL) {
            //  return 'Bearer ' + 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImtpZCI6InZIOCJ9.eyJzdWIiOjUsImlhdCI6MTU0MzM5MTI0MywiZXhwIjoxNTQzMzkzMDQzLCJwcmVmZXJyZWRfdXNlcm5hbWUiOiJob3Rjb3VwbGUiLCJyb2xlcyI6WyJVc2VyIl0sIk1lbWJlcklEIjoyNTkwfQ.jkhAeF5Kx7I7b72sTSH569CbwGPsJJsAB40Om6YK4vE8z7AHJxSqA9Aesw0EtvUwSAk-MNXlnltNJwoJdeQIeS-2uQRHS0YObMdUNmXEY6URvOerhdTUJt9V4aoSsIbNnYXLVtmp2ezaugxnVB26JT2YKqvcv-_g5cDGcKW6GLs'//config.rhpToken;
            if (this.isStorageCachingSupported) {
                if (axiosConfig.headers.isAnonymousAccessToken) {
                    //   console.log('rhpAnonymousToken'+config.rhpAnonymousToken);
                    token = webIMConfig.rhpAnonymousToken;
                } else {
                    token = storageHelper.getKey(constants.SESSIONS.RHP_TOKEN);
                }
            } else {
                if (webIMConfig.rhpToken) {
                    token = webIMConfig.rhpToken;
                } else {
                    if (axiosConfig.headers.isAnonymousAccessToken) {
                        //   console.log('rhpAnonymousToken'+config.rhpAnonymousToken);
                        token = webIMConfig.rhpAnonymousToken;
                    }
                }
            }
        } else if (axiosConfig.baseURL === Urls.BASE_URL) {
            if (storageHelper.storageCachingSupported()) {
                token = storageHelper.getKey(constants.SESSIONS.MESSENGER_TOKEN);
            } else {
                token = webIMConfig.token;
            }
        }
        return token;
    }

    getRefreshToken() {
        if (this.isStorageCachingSupported) {
            return storageHelper.getKey(constants.SESSIONS.RHP_REFRESH_TOKEN);
        }

        if (webIMConfig.rhpRefreshToken) {
            return webIMConfig.rhpRefreshToken;
        }

        return '';
    }

    setToken(value) {
        if (this.isStorageCachingSupported) {
            storageHelper.setKey(constants.SESSIONS.RHP_TOKEN, value);
        }
        //set token for native app
        webIMConfig.rhpToken = value;
    }

    refreshToken(originalBaseUrl, originalUrl, originalUrlResponse, originalToken) {
        const refreshToken = this.getRefreshToken();
        // console.log(refreshToken)
        if (refreshToken) {
            return axios
                .post(
                    Urls.RHP_GET_ACCESSTOKEN,
                    {
                        RefreshToken: refreshToken,
                    },
                    { baseURL: Urls.AUT_RHPURL }
                )
                .then(res => {
                    if (!this.handleExpireToken({
                        res,
                        originalBaseUrl,
                        originalUrl,
                        originalUrlResponse,
                        originalToken
                    })) {
                        logService.addLog({
                            message: `handleTokenExpired at url: ${encodeURI(originalUrl)}`,
                            type: constants.LOG_TYPE.DEBUG,
                            stackTrace: JSON.stringify({
                                originalUrl,
                                response: originalUrlResponse,
                                browserUrl: isNative ? '' : window.location.href,
                                browserOS: this.deviceType(),
                                platform: this.osType(),
                                currentToken: originalToken,
                                version: '1.1',
                                newToken: res && res.data ? res.data.AccessToken : '',
                            }),
                            category: constants.LOG_CATEGORY.TRACKING_REFRESH_TOKEN,
                        });
                        return res;
                    }
                    return Promise.resolve({ success: false });
                })
                .catch(err => {
                    this.onAddLogAndLogout({ stackTrace: 'Empty refresh Token', originalUrl });
                    return Promise.resolve({ success: false });
                });
        }

        this.onAddLogAndLogout({ stackTrace: 'Empty refresh Token', originalUrl });
        return Promise.resolve({ success: false });
    }

    handleExpireToken({ res, originalBaseUrl, originalUrl, originalUrlResponse, originalToken }) {
        // Token really expired
        // console.log({ res });
        if (res && res.data && !!res.data.AccessToken) {
            return false;
        }
        let stackTrace = '';
        stackTrace = `
                                  BaseURL ${originalBaseUrl} \n
                                  RefreshToken ${this.getRefreshToken()} \n
                                  Token ${originalToken} \n
                                  TestUrlResponse ${JSON.stringify(originalUrlResponse)} \n
                                  ResponseStatus ${res.data && res.data.ResponseStatus ? JSON.stringify(res.data.ResponseStatus) : null} \n
                                  StatusCode: ${res.status}
                                  duration:${res.duration}`;
        // Log Server
        this.onAddLogAndLogout({ stackTrace, originalUrl });
        return true;
    }

    onAddLogAndLogout({ stackTrace, originalUrl }) {
        if (typeof webIMConfig.logOut === 'function') {
            logService.addLog({
                message: `LOGOUT RefreshToken callBackURL: ${originalUrl}`,
                type: constants.LOG_TYPE.DEBUG,
                stackTrace,
                category: constants.LOG_CATEGORY.GLOBAL,
            });
            setTimeout(() => webIMConfig.logOut(), 1000);
        }
    }

    osType() {
        if (isNative) {
            return webIMConfig.DeviceOS;
        } else {
            return getOS();
        }
    }

    deviceType() {
        if (isNative) {
            return 'MobileApp';
        } else {
            const isMobileBrowser = phoneDetect();
            if (isMobileBrowser) {
                // Mobile Browser
                return 'Mobile';
            } else {
                return 'Desktop';
            }
        }
    }

    initResponseData() {
        return {
            success: false,
            statusCode: '',
            message: '',
            data: null,
            status: 0,
        };
    }

    createResponseData(_data) {
        //console.log(_data)
        let data = this.initResponseData();
        if (_data) {
            data.success = _data.statusCode === 200;
            data.status = _data.status;
            data.statusCode = _data.statusCode || '';
            if (_data.problem && _data.problem === 'TIMEOUT_ERROR') {
                data.message = 'Request timeout';
            }
            data.message = _data.message || '';
            if (Array.isArray(_data.data)) {
                data.data = { items: _data.data };
            } else {
                data.data = _data.data || null;
            }
        } else {
            data.status = 404;
            data.statusCode = '404';
            data.message = 'API error';
        }
        return data;
    }

    createRHPResponseData(res) {
        // console.log('RHP res',res)
        let data = this.initResponseData();
        if (res) {
            data.status = res.status;
            data.success =
                !(
                    (res.data && res.data.ResponseStatus && res.data.ResponseStatus.ErrorCode) ||
                    res.status < 200 ||
                    res.status >= 300 ||
                    (res.data && res.data.Success === false)
                ) ||
                res.ok ||
                res.status === 204;
            if (res.problem) {
                if (res.problem === 'TIMEOUT_ERROR') {
                    data.message = 'Request timeout';
                    data.status = 408;
                } else if (res.problem === 'NETWORK_ERROR') {
                    data.message = 'Network error';
                    data.status = 408;
                }
                // force assign status to 408 to support function checkAndRetry
            }

            if (res.data) {
                data.statusCode = res.data.ResponseStatus && res.data.ResponseStatus.ErrorCode ? res.data.ResponseStatus.ErrorCode : '200';
                data.message = res.data.ResponseStatus && res.data.ResponseStatus.Message ? res.data.ResponseStatus.Message : '';

                if (Array.isArray(res.data)) {
                    data.data = { items: res.data };
                } else {
                    data.data = res.data || null;
                }
            }
        } else {
            data.status = 404;
            data.statusCode = '404';
            data.message = 'API error';
        }

        return data;
    }

    createResponse(baseURL, res) {
        let response;
        if (baseURL === Urls.BASE_URL) {
            response = this.createResponseData(res?.data || {});
        } else {
            response = this.createRHPResponseData(res);
        }
        return response;
    }

    isInRetryRange(httpStatusCode, statusCodesToRetry) {
        let isInRange = false;
        for (const [min, max] of statusCodesToRetry) {
            const status = httpStatusCode;
            if (status >= min && status <= max) {
                isInRange = true;
                break;
            }
        }
        return isInRange;
    }
}
